import React from "react";
import { hoyISO, yaPaso } from "@/lib/zona";
import { useNavigate } from "react-router-dom";
import { CalendarClock, ChevronRight, BellRing, Clock, Plus, Repeat } from "lucide-react";
import { Asistente } from "@/components/recordatorios/Asistente";
import { fechaLarga } from "@/lib/fechas";
import { horaAvisoLabel } from "@/components/HoraAviso";
import { STATUS, STATUS_STYLES } from "@/mock/data";
import { useData } from "@/context/DataContext";
import { api } from "@/lib/api";
import { SeguimientoActions } from "@/components/SeguimientoActions";
import { itemsAgenda, grupoDe, GRUPOS, FILTROS } from "@/lib/agenda";

const ETIQUETA = {
  plan: ["Plan", "bg-[#E8F0EC] text-[#3A6B56]"],
  seguimiento: ["Seguimiento", "bg-[#E8F0EC] text-[#3A6B56]"],
  periodico: ["Periódico", "bg-[#E8F0EC] text-[#3A6B56]"],
  recordatorio: ["Recordatorio", "bg-[#E8F0EC] text-[#3A6B56]"],
};
const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const fmt = (iso) => { const d = new Date(iso + "T00:00:00"); return { day: d.getDate(), month: MONTHS[d.getMonth()], year: d.getFullYear() }; };

const useGestiones = () => {
  const [gestiones, setGestiones] = React.useState([]);
  const [ocs, setOcs] = React.useState([]);
  React.useEffect(() => {
    const cargar = () => {
      api.get("/gestiones").then((r) => setGestiones(r.data)).catch(() => {});
      api.get("/gestiones/ocurrencias").then((r) => setOcs(r.data)).catch(() => {});
    };
    cargar();
    window.addEventListener("aldia-gestiones", cargar);
    return () => window.removeEventListener("aldia-gestiones", cargar);
  }, []);
  return { gestiones, ocs };
};

const ItemAgenda = ({ i, idx }) => {
  const navigate = useNavigate();
  const d = fmt(i.date);
  const style = STATUS_STYLES[i.status] || STATUS_STYLES[STATUS.UPCOMING];
  const borde = i.atrasado ? "border-l-[#DC2626]" : style.leftBorder;
  const inner = (
    <>
      <div className="shrink-0 w-14 text-center">
        <p className="font-heading text-2xl font-extrabold text-[#1C2421] leading-none">{d.day}</p>
        <p className="text-sm font-semibold text-[#6E7A74] uppercase">{d.month}</p>
        <p className="text-xs text-[#9AA39D]">{d.year}</p>
      </div>
      <div className="w-px self-stretch bg-[#F2EDE4]" />
      <div className="flex-1 min-w-0">
        <span data-testid={`proximo-tipo-${i.id}`} className={`inline-block ${i.kind === "matter" ? `${style.badgeBg} ${style.badgeText}` : ETIQUETA[i.kind][1]} text-xs font-bold uppercase px-2 py-0.5 rounded-full`}>
          {i.kind === "matter" ? style.label : ETIQUETA[i.kind][0]}
        </span>
        <h3 className="font-heading font-bold text-lg text-[#1C2421] mt-1 leading-snug">{i.title}</h3>
        <p className="text-sm text-[#6E7A74] flex items-center gap-1.5 mt-0.5">
          <CalendarClock size={14} className="text-[#3A6B56] shrink-0" /> {i.sub || fechaLarga(i.date)}{i.time && i.sub && !i.sub.includes(i.time) ? ` · ${i.time}` : ""}
        </p>
      </div>
      <ChevronRight size={20} className="text-[#C4BDB0] shrink-0" />
    </>
  );
  const cls = `aldia-fade-up w-full bg-white border border-[#E5DFD5] ${borde} border-l-[6px] rounded-2xl p-4 shadow-sm`;
  if (i.seguimiento) {
    return (
      <div data-testid={`proximo-item-${i.id}`} className={cls} style={{ animationDelay: `${idx * 50}ms` }}>
        <button onClick={() => navigate(i.route)} className="w-full text-left flex items-center gap-4" data-testid={`proximo-open-${i.id}`}>{inner}</button>
        <SeguimientoActions recordatorioId={i.id} fecha={i.date} testidPrefix={`proximos-seg-${i.id}`} />
      </div>
    );
  }
  return (
    <button data-testid={`proximo-item-${i.id}`} onClick={() => navigate(i.route)} style={{ animationDelay: `${idx * 50}ms` }}
      className={`${cls} text-left flex items-center gap-4 hover:shadow-md transition-shadow`}>{inner}</button>
  );
};

const Grupo = ({ id, label, items }) => (
  <section className="space-y-3" data-testid={`agenda-grupo-${id}`}>
    <h2 className={`font-heading text-xl font-bold flex items-center gap-2 ${id === "atrasado" ? "text-[#B91C1C]" : "text-[#1C2421]"}`}>
      {id === "atrasado" && <Clock size={20} />} {label} <span className="text-base font-semibold text-[#6E7A74]">({items.length})</span>
    </h2>
    {id === "atrasado" && <p className="text-base text-[#4A5550] -mt-1">Su fecha ya pasó y siguen pendientes.</p>}
    {items.map((i, idx) => <ItemAgenda key={i.id} i={i} idx={idx} />)}
  </section>
);

const Programados = ({ lista }) => {
  const navigate = useNavigate();
  if (!lista.length) return null;
  return (
    <section className="pt-2" data-testid="proximos-programados">
      <h2 className="font-heading text-lg font-bold text-[#1C2421]">Avisos programados</h2>
      <p className="text-sm text-[#6E7A74] mt-0.5 mb-3">Asuntos al día. AL DÍA te avisará en estas fechas; no tienes que hacer nada ahora.</p>
      <div className="space-y-2.5">
        {lista.map((x) => (
          <button key={x.id} data-testid={`programado-item-${x.id}`} onClick={() => navigate(`/asunto/${x.id}`)}
            className="w-full text-left bg-[#FAF8F5] border border-[#E5DFD5] rounded-2xl p-4 flex items-center gap-3 hover:bg-white transition-colors">
            <span className="w-10 h-10 rounded-xl bg-[#F2EDE4] flex items-center justify-center shrink-0"><BellRing size={18} className="text-[#6E7A74]" /></span>
            <span className="flex-1 min-w-0">
              <span className="block font-semibold text-base text-[#1C2421] leading-snug">{x.matter.title}</span>
              <span className="block text-sm font-semibold text-[#4A5550] mt-0.5" data-testid={`programado-fecha-${x.id}`}>Aviso programado para el {fechaLarga(x.date)} · {horaAvisoLabel(x.time)}</span>
              {x.matter.due_label && <span className="block text-xs text-[#9AA39D]">{x.matter.due_label} · Documentación al día</span>}
            </span>
            <ChevronRight size={20} className="text-[#C4BDB0] shrink-0" />
          </button>
        ))}
      </div>
    </section>
  );
};

export default function Agenda() {
  const { matters, reminders } = useData();
  const { gestiones, ocs } = useGestiones();
  const [filtro, setFiltro] = React.useState("todo");
  const [nuevoPeriodico, setNuevoPeriodico] = React.useState(false);
  const navigate = useNavigate();
  const today = hoyISO();
  const todos = itemsAgenda({ matters, reminders, gestiones, ocs, today });
  const items = filtro === "todo" ? todos : todos.filter((i) => i.filtro === filtro);
  const enAgenda = new Set(todos.map((i) => i.id));
  // Asuntos al día con un aviso futuro realmente programado (el más cercano por asunto).
  const programados = Object.values((reminders || [])
    .filter((r) => r.status === "Programado" && r.reminder_date >= today && !yaPaso(r.reminder_date, r.reminder_time) && r.asunto_id && !enAgenda.has(r.asunto_id))
    .reduce((acc, r) => {
      const m = matters.find((x) => x.id === r.asunto_id && x.status === STATUS.CONTROLLED);
      if (m && (!acc[m.id] || r.reminder_date < acc[m.id].date)) acc[m.id] = { id: m.id, date: r.reminder_date, time: r.reminder_time || "", matter: m };
      return acc;
    }, {}))
    .sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="space-y-6" data-testid="agenda-page">
      <header className="aldia-fade-up">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1C2421]">Agenda</h1>
        <p className="text-lg text-[#4A5550] mt-1">Qué tienes que hacer y cuándo, de lo más cercano a lo más lejano.</p>
      </header>
      <button data-testid="agenda-anadir-recordatorio"
        onClick={() => window.dispatchEvent(new CustomEvent("aldia-add", { detail: { opcion: "recordatorio" } }))}
        className="w-full min-h-[56px] rounded-2xl bg-[#3A6B56] text-white font-semibold text-lg hover:bg-[#2E5645] transition-colors inline-flex items-center justify-center gap-2">
        <Plus size={24} /> Añadir recordatorio
      </button>
      <button data-testid="agenda-anadir-periodico" onClick={() => setNuevoPeriodico(true)}
        className="w-full min-h-[56px] -mt-3 rounded-2xl bg-white border-2 border-[#3A6B56] text-[#3A6B56] font-semibold text-lg hover:bg-[#E8F0EC] transition-colors inline-flex items-center justify-center gap-2">
        <Repeat size={22} /> Añadir recordatorio periódico
      </button>
      <button data-testid="agenda-ver-periodicos" onClick={() => navigate("/gestiones")}
        className="-mt-3 text-[#3A6B56] font-semibold text-base underline underline-offset-4 min-h-[44px]">Ver todos los recordatorios periódicos (también pausados y terminados)</button>
      {nuevoPeriodico && <Asistente onCerrar={() => { setNuevoPeriodico(false); window.dispatchEvent(new Event("aldia-gestiones")); }}
        onVer={(id) => navigate(`/gestiones/${id}`)} />}
      <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1" data-testid="agenda-filtros">
        {FILTROS.map(([id, l]) => (
          <button key={id} data-testid={`agenda-filtro-${id}`} onClick={() => setFiltro(id)} aria-pressed={filtro === id}
            className={`shrink-0 min-h-[40px] px-3.5 rounded-full text-sm font-semibold border transition-colors ${filtro === id ? "bg-[#3A6B56] text-white border-[#3A6B56]" : "bg-white text-[#4A5550] border-[#E5DFD5]"}`}>
            {l}
          </button>
        ))}
      </div>
      {!items.length && (
        <p data-testid="proximos-vacio" className="text-lg text-[#6E7A74] bg-white border border-[#E5DFD5] rounded-2xl p-6 text-center">
          {filtro === "todo" ? "No tienes nada pendiente en tu agenda." : "No hay nada de este tipo en tu agenda."}
        </p>
      )}
      <div className="space-y-7" data-testid="proximos-list">
        {GRUPOS.map(([id, label]) => {
          const g = items.filter((i) => grupoDe(i, today) === id);
          return g.length ? <Grupo key={id} id={id} label={label} items={g} /> : null;
        })}
      </div>
      {filtro === "todo" && <Programados lista={programados} />}
    </div>
  );
}
